import { useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { BookType } from 'lucide-react';
import { getGenreInfo, getSubgenreInfo, getTopLevelGenreInfo } from '@/lib/utils/genre-utils';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

interface GenreInfoModalProps {
    isOpen: boolean;
    onClose: () => void;
    form: string | undefined;
    genre: string | undefined;
}

function getGenreColor(genre: string, isDarkMode: boolean) {
    const colors = {
        Narrative: isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800',
        Poetry: isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800',
        Wisdom: isDarkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800',
        Prophecy: isDarkMode ? 'bg-red-900 text-red-200' : 'bg-red-100 text-red-800',
        Epistle: isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800',
        Law: isDarkMode ? 'bg-gray-900 text-gray-200' : 'bg-gray-100 text-gray-800',
    };
    return colors[genre as keyof typeof colors] || colors.Narrative;
}

export function GenreInfoModal({ isOpen, onClose, form, genre }: GenreInfoModalProps) {
    const isDarkMode = useDarkMode();
    // Get the genre hierarchy info
    const { genreInfo, subgenreInfo, topLevelGenreInfo } = useMemo(() => {
        if (!genre) return { genreInfo: null, subgenreInfo: null, topLevelGenreInfo: null };

        const topLevel = getTopLevelGenreInfo(genre);

        // If we have a form, try to get the full hierarchy
        if (form) {
            // We need to find the subgenre that contains this form
            const subgenres = topLevel?.subgenres || [];
            for (const subgenre of subgenres) {
                const formItem = subgenre.forms.find(f => f.form === form);
                if (formItem) {
                    return {
                        genreInfo: { genre, subgenre: subgenre.subgenre, ...formItem },
                        subgenreInfo: { genre, ...subgenre },
                        topLevelGenreInfo: topLevel
                    };
                }
            }
        }

        return { genreInfo: null, subgenreInfo: null, topLevelGenreInfo: topLevel };
    }, [genre, form]);

    if (!genre) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogTitle className="sr-only">Literary Form Information</DialogTitle>
                <DialogDescription className="sr-only">
                    Detailed study information about the literary genre and form of this passage.
                </DialogDescription>
                <DialogHeader>
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        <BookType className="h-5 w-5" />
                        Literary Form Information
                    </div>
                </DialogHeader>

                <div className="space-y-6">
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2">
                        {genre && (
                            <Badge variant="outline" className={`${getGenreColor(genre, isDarkMode)} text-sm`}>
                                {genre}
                            </Badge>
                        )}
                        {subgenreInfo && (
                            <Badge variant="outline" className="text-sm">
                                {subgenreInfo.subgenre}
                            </Badge>
                        )}
                        {form && (
                            <Badge variant="outline" className="text-sm font-semibold">
                                {form}
                            </Badge>
                        )}
                    </div>

                    {/* Form Information (most specific) */}
                    {genreInfo && (
                        <div className="space-y-3">
                            <h3 className="font-semibold text-base border-b pb-2">{form}</h3>
                            <p className="text-sm text-muted-foreground">{genreInfo.long_description}</p>

                            <div>
                                <h5 className="font-semibold text-sm mb-1">Interpreting Guidelines</h5>
                                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md border">
                                    {genreInfo.interpreting_guidelines}
                                </p>
                            </div>

                            <div>
                                <h5 className="font-semibold text-sm mb-1">Primary Purpose</h5>
                                <p className="text-sm text-muted-foreground">{genreInfo.primary_purpose}</p>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <h5 className="font-semibold text-sm mb-2">Key Characteristics</h5>
                                    <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                                        {genreInfo.key_characteristics.map((char: string, i: number) => (
                                            <li key={i}>{char}</li>
                                        ))}
                                    </ul>
                                </div>
                                <div>
                                    <h5 className="font-semibold text-sm mb-2">Common Pitfalls</h5>
                                    <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                                        {genreInfo.common_pitfalls.map((pitfall: string, i: number) => (
                                            <li key={i}>{pitfall}</li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Subgenre Information */}
                    {subgenreInfo && !genreInfo && (
                        <div className="space-y-3">
                            <h3 className="font-semibold text-base border-b pb-2">{subgenreInfo.subgenre}</h3>
                            <p className="text-sm text-muted-foreground">{subgenreInfo.long_description}</p>

                            <div>
                                <h5 className="font-semibold text-sm mb-1">Interpreting Guidelines</h5>
                                <p className="text-sm text-muted-foreground bg-muted/50 p-3 rounded-md border">
                                    {subgenreInfo.interpreting_guidelines}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Top-level Genre Information */}
                    {topLevelGenreInfo && (
                        <div className="space-y-3 pt-4 border-t">
                            <h3 className="font-semibold text-base">About {genre}</h3>
                            <p className="text-sm text-muted-foreground">{topLevelGenreInfo.short_description}</p>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}

